import { useCallback } from "react";
import { useHttp } from "../Providers/Common/HttpProvider.jsx";

/** Хук для работы с API Новой Почты через backend-прокси мобильного API.
    Использует общий HttpProvider, поэтому автоматически подставляет токен авторизации, локаль и другие заголовки.
*/
export function useNovaPoshtaApi() {
  const { post } = useHttp();

  /**
   * Поиск городов Новой Почты по части названия.
   * @param {string} query
   * @returns {Promise<Object[]>}
   */
  const searchNovaPoshtaCities = useCallback(
    async (query) => {
      // Если строка слишком короткая, не делаем лишних запросов к backend-прокси
      if (!query || query.trim().length < 3) {
        return [];
      }

      // Формируем payload в формате, который ожидает API Новой Почты
      const payload = {
        modelName: "Address",
        calledMethod: "searchSettlements",
        methodProperties: {
          CityName: query.trim(),
          Limit: 100,
        },
      };

      try {
        // Отправляем запрос на backend-прокси Новой Почты.
        // HttpProvider сам подставит заголовок authorization, locale и другие нужные заголовки.
        const data = await post("nova-poshta", payload, null, {}, true);

        // Если backend вернул ошибку или пустой ответ, считаем, что городов нет
        if (!data || !data.success || !Array.isArray(data.data) || data.data.length === 0) {
          return [];
        }

        const first = data.data[0];
        if (!first || !Array.isArray(first.Addresses)) {
          return [];
        }

        return first.Addresses;
      } catch (e) {
        // Ошибки сети/парсинга не должны ронять приложение, просто вернём пустой массив
        return [];
      }
    },
    [post],
  );

  /**
   * Получение списка отделений Новой Почты для выбранного города.
   * @param {string} cityRef
   * @returns {Promise<Object[]>}
   */
  const getNovaPoshtaWarehouses = useCallback(
    async (cityRef) => {
      // Без корректного идентификатора города нет смысла запрашивать отделения
      if (!cityRef || !cityRef.trim()) {
        return [];
      }

      const payload = {
        modelName: "Address",
        calledMethod: "getWarehouses",
        methodProperties: {
          SettlementRef: cityRef.trim(),
          Limit: 20000,
        },
      };

      try {
        const data = await post("nova-poshta", payload, null, {}, true);

        if (!data || !data.success || !Array.isArray(data.data)) {
          return [];
        }

        return data.data;
      } catch (e) {
        // На ошибках сети также просто возвращаем пустой список
        return [];
      }
    },
    [post],
  );

  // Возвращаем методы, чтобы использовать их в компонентах (например, в форме заказа)
  return { searchNovaPoshtaCities, getNovaPoshtaWarehouses };
}

export default useNovaPoshtaApi;
